import { boot } from '@dazn/bootstrap-lib';

import { chapterNames } from '../configuration/constants';

import { createChapterHistory } from './chapterHistory/chapterHistory';
import { computeChapterURL } from './chapterHistory/computeChapterURL';
import { getChapterIndexURL } from './chapterHistory/getChapterIndexURL';
import { handlePopstate } from './chapterHistory/handlePopstate';
import { loadChapter } from './chapterLoader/chapterLoader';
import { createInvalidChapterError } from './chapterLoader/errors';
import {
    Configuration,
    ErrorHandlingModule,
    InitializationModule,
    StartupModule,
    StorageModule,
    UserModule,
    Utilities,
} from './global-modules';
import { createLifecycleHooks } from './lifecycle/lifecycle';
import { getChapterCountry as _getChapterCountry } from './locale/getChapterCountry';
import { getLanguage } from './locale/getLanguage';
import { initGetResourceStrings } from './resourceStrings/getResourceStrings';
import {
    getChapterInfoFromPath,
    verifyChapterAuthorization as _verifyChapterAuthorization,
} from './utils/getChapterFromPath';
import { getQuery } from './utils/location';
import { normalizeChapter } from './utils/normalizeChapter';

type Win = typeof window;

interface Feature {
    isEnabledAnonymousOptimizely: (
        experimentName: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => boolean | null;
    getVariableStringAnonymousOptimizely: (
        experimentKey: string,
        variableKey: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => string | null;
}

export type CreateNavigationModuleProps = {
    window: Win;
    configuration: Configuration;
    startupModule: StartupModule;
    util: Utilities;
    userModule: UserModule;
    errorModule: ErrorHandlingModule;
    storageModule: StorageModule;
    initializationModule: InitializationModule;
    features: Feature;
    getBrandName: () => boot.brand.BrandName;
    getCustomHeaders?: () => {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    };
};

type ForcedLanguage = {
    language: string;
    country: string;
};

const languageMap: { [key: string]: string } = {
    ar: 'SA', // Arabic → Saudi Arabia
    zh: 'TW', // Chinese → Taiwan
    nl: 'BE', // Dutch → Belgium
    en: 'US', // English → United States
    fr: 'FR', // French → France
    de: 'DE', // German → Germany
    he: 'IL', // Hebrew → Israel
    it: 'IT', // Italian → Italy
    ja: 'JP', // Japanese → Japan
    pt: 'BR', // Portuguese → Brazil
    es: 'ES', // Spanish → Spain
};

const getForcedLanguage = (): ForcedLanguage | undefined => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('forcedLanguage');
    if (!lang) {
        return;
    }
    const region = languageMap[lang];
    if (!region) {
        return;
    }
    const reg = params.get('forcedRegion');
    return {
        language: lang,
        country: reg ?? region,
    };
};

/**
 * Responsible for navigation, routing, lifecycle events, locale, and chapter utilities
 */
export function createNavigationModule({
    window,
    configuration,
    startupModule,
    userModule,
    errorModule,
    storageModule,
    util,
    initializationModule,
    features,
    getBrandName,
    getCustomHeaders,
}: CreateNavigationModuleProps) {
    let isLoaded = false;

    const lifecycleHooks = createLifecycleHooks(errorModule);

    const getStartupNativeCountryOrMoon = () =>
        util.getNativeCountryOrMoon(startupModule.getStartupDataCountry());

    const getStartupNativeCountryOrMoonOrMena = () =>
        util.getNativeCountryOrMoonOrMena(
            startupModule.getStartupDataCountry(),
        );

    const getChapterFromPath = (pathname: string) => {
        const verifiedChapter = getChapterInfoFromPath({
            configuration,
            isAuthorized: userModule.isAuthorized(),
            isActive: userModule.isActive,
            pathname,
            startupNativeCountryOrMoon: getStartupNativeCountryOrMoon(),
            startupNativeCountryOrMoonOrMena:
                getStartupNativeCountryOrMoonOrMena(),
            isVip: userModule.isVip,
        }).verifiedChapterName;

        // Foxsports is a prelogin-only brand and should only allow news chapter
        const brandName = getBrandName();
        if (
            brandName === 'foxsports' &&
            verifiedChapter !== chapterNames.NEWS
        ) {
            return chapterNames.NEWS;
        }

        return verifiedChapter;
    };

    const getChapterCountry = () => {
        const countryOverride = getForcedLanguage()?.country;
        if (!!countryOverride) {
            return countryOverride;
        }
        return _getChapterCountry(
            window.location.pathname,
            startupModule.getStartupDataCountry(),
            startupModule.isPortable,
            configuration.chapters,
            getChapterFromPath,
            userModule.getSignUpCountry(),
            userModule.getContentCountry(),
        );
    };

    const verifyChapterAuthorization = (
        chapterName: boot.config.ChapterName,
    ) => {
        const brandName = getBrandName();

        // Foxsports is a prelogin-only brand and should only allow news chapter
        if (brandName === 'foxsports' && chapterName !== chapterNames.NEWS) {
            return chapterNames.NEWS;
        }

        // Foxtelgnc brand-specific rules
        if (brandName === 'foxtelgnc') {
            // Don't allow landingpage chapter - redirect to catalog
            if (chapterName === chapterNames.LANDING_PAGE) {
                chapterName = chapterNames.CATALOG;
            }

            // Catalog requires authentication for foxtelgnc
            if (
                chapterName === chapterNames.CATALOG &&
                !userModule.isAuthorized()
            ) {
                return configuration.chapters.loginChapter;
            }
        }

        return _verifyChapterAuthorization({
            configuration: configuration.chapters,
            isActive: userModule.isActive,
            isAuthorized: userModule.isAuthorized(),
            chapters: [chapterName],
            isVip: userModule.isVip,
        });
    };

    // Cached Language is the language we have stored in LocalStorage
    let cachedLanguage: boot.config.Language | null = null;

    const getChapterLanguage = () => {
        const languageOverride = getForcedLanguage()?.language;
        if (!!languageOverride) {
            return languageOverride;
        }
        const lang = getLanguage({
            startupSupportedLanguages:
                startupModule.getAllowedLanguages() ?? [],
            startupLanguage: startupModule.getRegionLanguage() || '',
            cachedLanguage,
            userSupportedLanguages: userModule.getSupportedLanguages(),
            userProfileLanguage: userModule.getUserLanguage(),
            pathname: window.location.pathname,
            skipUrlLanguageValidation:
                configuration.chapters.getsCountryFromUrl(
                    getChapterFromPath(window.location.pathname),
                ),
            isUserLanguagePriority: !userModule.isProfileCached(),
        });

        // TODO: forbid case where isLoaded === false
        if (!isLoaded) {
            return lang;
        }

        // only save the language in LocalStorage if it changed
        if (lang !== cachedLanguage) {
            storageModule.saveLanguage(lang);
            cachedLanguage = lang;
        }
        return lang;
    };

    const downloadChapterAndAddInPage = (
        chapterName: boot.config.ChapterName,
        chapterCountry: boot.config.AnyCountry,
        garbageCollector: () => void,
    ) => {
        if (chapterName.includes('auth_signin')) {
            chapterName = chapterNames.AUTH;
        }
        const { chapterURL, baseURL } = getChapterIndexURL({
            window,
            chapterName,
            resourceURL: configuration.resourcesBaseUrl,
            region: util.getNativeCountryOrMoon(getChapterCountry()),
        });
        return loadChapter({
            window,
            url: chapterURL,
            baseUrl: baseURL,
            chapterName,
            chapterCountry,
            triggers: lifecycleHooks.triggers,
            garbageCollector,
            selectorsExcludedFromCleaning: util.selectorsExcludedFromCleaning,
        });
    };

    const getChapterUrl = (
        normalizedChapter: boot.config.NormalChapterConfig,
        chapterCountry: boot.config.AnyCountry,
        chapterLanguage: boot.config.Language,
    ) => {
        const { chapterName } = normalizedChapter;
        if (chapterName === chapterNames.ERROR) {
            return `${window.location.pathname}${getQuery(window.location)}`;
        }

        const chapterPathWithoutAppUrlPrefix =
            normalizedChapter.chapterPath &&
            normalizedChapter.chapterPath.replace(
                configuration.appUrlPrefix,
                '',
            );

        const chapterUrlPath = computeChapterURL({
            location: window.location,
            chaptersMap: configuration.chapters.combinedChapterMap,
            country: chapterCountry,
            language: chapterLanguage,
            chapterHomepage: configuration.chapters.homePage(chapterName),
            withLanguageInUrl:
                configuration.chapters.hasLanguageInUrl(chapterName),
            chapterFromUrl: getChapterFromPath(window.location.pathname),
            chapterName: normalizedChapter.chapterName,
            chapterPath: chapterPathWithoutAppUrlPrefix,
            rewriteUrlLocale:
                configuration.chapters.rewriteUrlLocale(chapterName),
            rewriteUrlLocaleOverrideMap:
                configuration.chapters.rewriteUrlLocaleOverrideMap(chapterName),
        });
        return configuration.appUrlPrefix + chapterUrlPath;
    };

    const getResourceStrings = initGetResourceStrings({
        configuration,
        fetch: window.fetch,
        getChapterCountry,
        getChapterLanguage,
        getPlatform: initializationModule.getPlatform,
        getResourceStringsEndpoint: util.getResourceStringsEndpoint,
        features,
        getBrandName,
        getCustomHeaders,
    });

    const { getCurrentChapter, setCurrentChapter, onChapterInstalled } =
        createChapterHistory({
            window,
            configuration,
            errorModule,
            userModule,
            verifyChapterAuthorization,
            getChapterCountry,
            setChapterVersion: initializationModule.setChapterVersion,
            getChapterLanguage,
            downloadChapterAndAddInPage,
            getChapterUrl,
            getResourceStrings,
            triggerChapterDidLoadEvent: lifecycleHooks.triggers.didLoadEvents,
            getStartupNativeCountryOrMoon,
            getStartupNativeCountryOrMoonOrMena,
            isVip: userModule.isVip,
            getBrandName,
        });

    return {
        run: () =>
            storageModule.loadLanguage().then(storedLanguage => {
                isLoaded = true;
                if (storedLanguage) {
                    cachedLanguage = storedLanguage;
                }
                return getResourceStrings();
            }),
        /**
         * Returns the resource strings for the loaded locale
         */
        getResourceStrings,
        /**
         * Returns the current locale language
         */
        getLanguage: getChapterLanguage,
        /**
         * Returns the current locale country
         */
        getChapterCountry,
        /**
         * Returns the current chapter loaded
         */
        getCurrentChapter: ():
            | boot.config.ChapterName
            | boot.config.NormalChapterConfig => {
            const curChapter = getCurrentChapter();
            return curChapter.chapterData ? curChapter : curChapter.chapterName;
        },
        /**
         * Sets/Loads a new chapter
         */
        setCurrentChapter,
        /**
         * {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event}
         */
        handlePopstate: () => {
            const currentPath = window.location.pathname;
            handlePopstate(
                setCurrentChapter,
                getCurrentChapter().chapterName,
                getChapterFromPath(currentPath),
                currentPath,
            );
        },
        /**
         * Returns the chapter given a path
         */
        getChapterFromPath,
        /**
         * A map of hook functions which can queue tasks to work between certain navigation states
         */
        lifecycleEvents: lifecycleHooks.events,
        /**
         * Helper function to setup and load the initial chapter
         */
        loadInitialChapter: (deepLink: boot.config.DeepLink | null = null) => {
            const chapterPath = window.location.pathname;
            if (!window.daznSSR) {
                const chapterNameFromPath = getChapterFromPath(chapterPath);
                if (!chapterNameFromPath) {
                    throw createInvalidChapterError(
                        `Unable to compute chapter name from path ${chapterPath}`,
                    );
                }

                if (
                    !startupModule.checkRegionAvailability() &&
                    !userModule.isAuthorized()
                ) {
                    return setCurrentChapter({
                        chapterName: chapterNames.SIGN_IN,
                    });
                }

                if (deepLink) {
                    return setCurrentChapter(deepLink);
                }

                lifecycleHooks.triggers.willStartEvents();
                return setCurrentChapter({ chapterPath });
            }
            const { chapterName, chapterCountry, chapterVersion } =
                window.daznSSR;
            const normalChapter = deepLink
                ? normalizeChapter(deepLink, chapterPath)
                : null;

            onChapterInstalled(
                {
                    chapterName,
                    chapterData: normalChapter
                        ? normalChapter.chapterData
                        : null,
                    chapterPath,
                },
                {
                    chapterCountry,
                    chapterVersion,
                },
            );
            return Promise.resolve();
        },
    };
}
